import { ServiceHistory } from '@/types';
import { HISTORY_BAR_COLORS, HISTORY_BAR_LABELS } from '@/services/indicators';
import { BUCKET_MS } from '@/services/history';

interface ServiceHistoryStripProps {
  history?: ServiceHistory;
  /** Health could not be read at all — do not draw a history we can't back. */
  unknown?: boolean;
}

function describe(startsAt: Date, count: number, criticalCount: number): string {
  const from = startsAt.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const to = new Date(startsAt.getTime() + BUCKET_MS).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (count === 0) return `${from}–${to}: no errors reported`;
  const critical = criticalCount > 0 ? `, ${criticalCount} critical` : '';
  return `${from}–${to}: ${count} error${count !== 1 ? 's' : ''}${critical}`;
}

/**
 * The element that makes a page feel like a status page: a thin bar per unit of
 * time, oldest on the left.
 *
 * Ours covers 24 HOURS, not the customary 90 days, and it is labelled "fault
 * history" rather than "uptime". Both are honesty constraints, not stylistic
 * ones. The backend prunes the public aggregate at 24h, and the aggregate
 * records failures only — there is no probe, no success count and no request
 * volume anywhere in this system — so neither a 90-day span nor an uptime
 * PERCENTAGE can be computed from data we actually hold. Drawing ninety green
 * bars would be inventing eighty-nine days of good news.
 *
 * Quiet hours are therefore slate rather than green: "nothing was reported" is
 * all we know, and it is a weaker claim than "it was up".
 */
export function ServiceHistoryStrip({ history, unknown }: ServiceHistoryStripProps) {
  if (unknown || !history || history.buckets.length === 0) {
    return (
      <div
        className="flex h-4 items-stretch gap-px rounded-sm bg-slate-100"
        title="No history available"
        aria-hidden
      />
    );
  }

  const total = history.buckets.reduce((sum, b) => sum + b.count, 0);
  const first = history.buckets[0].start;
  const summary =
    total === 0
      ? 'No errors reported in the last 24 hours'
      : `${total} error${total !== 1 ? 's' : ''} reported in the last 24 hours`;

  return (
    <div className="space-y-1">
      <div
        className="flex h-4 items-stretch gap-px"
        role="img"
        aria-label={summary}
      >
        {history.buckets.map((bucket) => (
          <span
            key={bucket.start.getTime()}
            className={`flex-1 rounded-[1px] ${HISTORY_BAR_COLORS[bucket.level]}`}
            title={describe(bucket.start, bucket.count, bucket.criticalCount)}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-[11px] leading-none text-slate-400">
        <span>
          {history.truncated
            ? `from ${first.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} (older data dropped)`
            : '24h ago'}
        </span>
        <span>now</span>
      </div>
    </div>
  );
}

/**
 * The strip's key. Spelled out in words because the bars carry no text of their
 * own, and because "quiet" needs the caveat attached to it.
 */
export function HistoryStripLegend() {
  const levels = ['quiet', 'errors', 'critical'] as const;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
      <span className="font-medium text-slate-600">Fault history · 24h · 15 min per bar</span>
      {levels.map((level) => (
        <span key={level} className="flex items-center gap-1.5">
          <span className={`h-3 w-1.5 rounded-[1px] ${HISTORY_BAR_COLORS[level]}`} aria-hidden />
          {HISTORY_BAR_LABELS[level]}
        </span>
      ))}
    </div>
  );
}
